import {
  FilterLayerNavigation,
  type NavigationStateEntity,
} from '@/domains/navigation/navigation.entity';
import type { NavigationRepository } from '@/domains/navigation/navigation.repository';

/**
 * Use Case para manejar la navegación entre capas de filtro mediante breadcrumbs
 * Cumple SRP: Solo se encarga de la lógica de navegación de breadcrumbs
 *
 * Responsabilidades:
 * - Agregar nuevas capas al stack de navegación
 * - Validar límites de profundidad de capas de filtro
 * - Navegar hacia atrás en el stack
 * - Obtener estado actual de navegación
 * - Resetear la navegación completa
 */
export class ManageBreadcrumbNavigationUseCase {
  constructor(private readonly navigationRepository: NavigationRepository) {}

  /**
   * Agrega una nueva capa de filtro al breadcrumb de navegación
   * @param title - Título de la capa de navegación
   * @param path - Ruta de la capa
   * @param params - Parámetros opcionales de la ruta
   */
  addFilterLayer(
    title: string,
    path: string,
    params?: Record<string, string>,
  ): NavigationStateEntity {
    const newBreadcrumb = FilterLayerNavigation.createBreadcrumb(
      title,
      path,
      params,
    );
    const currentState = this.navigationRepository.getCurrentNavigationState();
    // NUEVA LÓGICA: Detectar si es una caracterización
    const isCharacterization = path.includes('/caracterizacion/');

    if (isCharacterization) {
      // Buscar si ya existe alguna caracterización en el stack
      const existingCharacterizationIndex = currentState.breadcrumbs.findIndex(
        (b) => b.path.includes('/caracterizacion/'),
      );
      // Si existe y NO es la misma, removerla
      if (existingCharacterizationIndex !== -1) {
        const existingPath =
          currentState.breadcrumbs[existingCharacterizationIndex].path;

        // Solo reemplazar si es diferente
        if (existingPath !== path) {
          // Navegar hacia atrás para remover caracterización anterior
          const indexBeforeCharacterization = existingCharacterizationIndex - 1;
          if (indexBeforeCharacterization >= 0) {
            this.navigationRepository.navigateBackToIndex(
              indexBeforeCharacterization,
            );
          } else {
            // Si era el primer breadcrumb, limpiar todo
            this.navigationRepository.clearNavigationStack();
          }
        } else {
          // Es la misma ruta, no hacer nada (prevenir duplicado)
          return this.navigationRepository.getCurrentNavigationState();
        }
      }
    }
    // Obtener estado actualizado después de posible limpieza
    const updatedState = this.navigationRepository.getCurrentNavigationState();
    const updatedBreadcrumbs = [...updatedState.breadcrumbs, newBreadcrumb];
    if (!FilterLayerNavigation.validateBreadcrumbStack(updatedBreadcrumbs)) {
      throw new Error('Excede el límite de capas de filtro permitidas');
    }
    if (newBreadcrumb.isFilterLayer) {
      this.navigationRepository.addBreadcrumb(newBreadcrumb);
    }
    return this.navigationRepository.getCurrentNavigationState();
  }

  /**
   * Navega hacia atrás hasta un nivel específico del breadcrumb
   * @param targetIndex - Índice del nivel de breadcrumb al cual retornar
   */
  navigateBack(targetIndex: number): NavigationStateEntity {
    return this.navigationRepository.navigateBackToIndex(targetIndex);
  }

  /**
   * Obtiene el estado actual de navegación
   */
  getCurrentNavigation(): NavigationStateEntity {
    return this.navigationRepository.getCurrentNavigationState();
  }

  /**
   * Limpia toda la navegación (volver a inicio)
   */
  resetNavigation(): void {
    this.navigationRepository.clearNavigationStack();
  }
}
